import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const InvoiceGenerator = () => {
  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: '',
    from: {
      name: '',
      address: '',
      email: '',
      phone: ''
    },
    to: {
      name: '',
      address: '',
      email: '',
      phone: ''
    },
    date: new Date().toISOString().split('T')[0],
    paymentTerms: 'Net 30',
    dueDate: '',
    items: [
      { description: '', quantity: 1, rate: 0, amount: 0 }
    ],
    notes: '',
    terms: 'Payment is due within the specified payment terms. Late payments may incur additional charges.'
  });

  // Calculate due date based on payment terms
  useEffect(() => {
    if (invoiceData.date && invoiceData.paymentTerms) {
      const invoiceDate = new Date(invoiceData.date);
      let daysToAdd = 30; // Default

      if (invoiceData.paymentTerms.includes('15')) daysToAdd = 15;
      else if (invoiceData.paymentTerms.includes('30')) daysToAdd = 30;
      else if (invoiceData.paymentTerms.includes('60')) daysToAdd = 60;
      else if (invoiceData.paymentTerms.includes('Due on receipt')) daysToAdd = 0;

      const dueDate = new Date(invoiceDate.getTime() + (daysToAdd * 24 * 60 * 60 * 1000));
      setInvoiceData(prev => ({
        ...prev,
        dueDate: dueDate.toISOString().split('T')[0]
      }));
    }
  }, [invoiceData.date, invoiceData.paymentTerms]);

  const updateField = (section, field, value) => {
    if (section) {
      setInvoiceData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    } else {
      setInvoiceData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const updateItem = (index, field, value) => {
    const updatedItems = [...invoiceData.items];
    updatedItems[index][field] = value;
    
    // Calculate amount
    if (field === 'quantity' || field === 'rate') {
      updatedItems[index].amount = updatedItems[index].quantity * updatedItems[index].rate;
    }
    
    setInvoiceData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const addItem = () => {
    setInvoiceData(prev => ({
      ...prev,
      items: [...prev.items, { description: '', quantity: 1, rate: 0, amount: 0 }]
    }));
  };

  const removeItem = (index) => {
    if (invoiceData.items.length > 1) {
      setInvoiceData(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    }
  };

  const calculateSubtotal = () => {
    return invoiceData.items.reduce((sum, item) => sum + item.amount, 0);
  };

  const generateInvoiceNumber = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
    return `INV-${year}${month}-${random}`;
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <InvoiceContainer>
      <Header>
        <Title>Invoice Generator</Title>
        <ActionButtons>
          <Button onClick={() => updateField(null, 'invoiceNumber', generateInvoiceNumber())}>
            Generate Invoice #
          </Button>
          <PrintButton onClick={handlePrint}>Print Invoice</PrintButton>
        </ActionButtons>
      </Header>

      <InvoiceForm>
        {/* Invoice Header */}
        <InvoiceHeader>
          <HeaderLeft>
            <Logo>B&B Global Solutions</Logo>
            <InvoiceTitle>INVOICE</InvoiceTitle>
          </HeaderLeft>
          <HeaderRight>
            <Field>
              <Label>Invoice Number:</Label>
              <Input
                type="text"
                value={invoiceData.invoiceNumber}
                onChange={(e) => updateField(null, 'invoiceNumber', e.target.value)}
                placeholder="INV-2024-001"
              />
            </Field>
            <Field>
              <Label>Date:</Label>
              <Input
                type="date"
                value={invoiceData.date}
                onChange={(e) => updateField(null, 'date', e.target.value)}
              />
            </Field>
            <Field>
              <Label>Due Date:</Label>
              <Input
                type="date"
                value={invoiceData.dueDate}
                onChange={(e) => updateField(null, 'dueDate', e.target.value)}
              />
            </Field>
          </HeaderRight>
        </InvoiceHeader>

        {/* From and To */}
        <PartiesContainer>
          <Party>
            <PartyTitle>From:</PartyTitle>
            <Input
              type="text"
              placeholder="Company Name"
              value={invoiceData.from.name}
              onChange={(e) => updateField('from', 'name', e.target.value)}
            />
            <TextArea
              placeholder="Address"
              rows="3"
              value={invoiceData.from.address}
              onChange={(e) => updateField('from', 'address', e.target.value)}
            />
            <Input
              type="email"
              placeholder="Email"
              value={invoiceData.from.email}
              onChange={(e) => updateField('from', 'email', e.target.value)}
            />
            <Input
              type="tel"
              placeholder="Phone"
              value={invoiceData.from.phone}
              onChange={(e) => updateField('from', 'phone', e.target.value)}
            />
          </Party>
          
          <Party>
            <PartyTitle>To:</PartyTitle>
            <Input
              type="text"
              placeholder="Client Name"
              value={invoiceData.to.name}
              onChange={(e) => updateField('to', 'name', e.target.value)}
            />
            <TextArea
              placeholder="Address"
              rows="3"
              value={invoiceData.to.address}
              onChange={(e) => updateField('to', 'address', e.target.value)}
            />
            <Input
              type="email"
              placeholder="Email"
              value={invoiceData.to.email}
              onChange={(e) => updateField('to', 'email', e.target.value)}
            />
            <Input
              type="tel"
              placeholder="Phone"
              value={invoiceData.to.phone}
              onChange={(e) => updateField('to', 'phone', e.target.value)}
            />
          </Party>
        </PartiesContainer>

        {/* Payment Terms */}
        <PaymentSection>
          <Field>
            <Label>Payment Terms:</Label>
            <Select
              value={invoiceData.paymentTerms}
              onChange={(e) => updateField(null, 'paymentTerms', e.target.value)}
            >
              <option value="Due on receipt">Due on receipt</option>
              <option value="Net 15">Net 15</option>
              <option value="Net 30">Net 30</option>
              <option value="Net 60">Net 60</option>
            </Select>
          </Field>
        </PaymentSection>

        {/* Items Table */}
        <ItemsSection>
          <ItemsHeader>
            <HeaderCell width="40%">Description</HeaderCell>
            <HeaderCell width="15%">Quantity</HeaderCell>
            <HeaderCell width="20%">Rate</HeaderCell>
            <HeaderCell width="20%">Amount</HeaderCell>
            <HeaderCell width="5%">Action</HeaderCell>
          </ItemsHeader>
          
          {invoiceData.items.map((item, index) => (
            <ItemRow key={index}>
              <ItemCell width="40%">
                <Input
                  type="text"
                  placeholder="Item description"
                  value={item.description}
                  onChange={(e) => updateItem(index, 'description', e.target.value)}
                />
              </ItemCell>
              <ItemCell width="15%">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={item.quantity}
                  onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                />
              </ItemCell>
              <ItemCell width="20%">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={item.rate}
                  onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                />
              </ItemCell>
              <ItemCell width="20%">
                <AmountDisplay>${item.amount.toFixed(2)}</AmountDisplay>
              </ItemCell>
              <ItemCell width="5%">
                <RemoveButton onClick={() => removeItem(index)}>×</RemoveButton>
              </ItemCell>
            </ItemRow>
          ))}
          
          <AddItemButton onClick={addItem}>+ Add Item</AddItemButton>
        </ItemsSection>

        {/* Total */}
        <TotalSection>
          <TotalRow>
            <TotalLabel>Subtotal:</TotalLabel>
            <TotalAmount>${calculateSubtotal().toFixed(2)}</TotalAmount>
          </TotalRow>
          <TotalRow>
            <TotalLabel>Total:</TotalLabel>
            <TotalAmount>${calculateSubtotal().toFixed(2)}</TotalAmount>
          </TotalRow>
        </TotalSection>

        {/* Notes and Terms */}
        <NotesSection>
          <Field>
            <Label>Notes:</Label>
            <TextArea
              rows="3"
              placeholder="Additional notes or instructions"
              value={invoiceData.notes}
              onChange={(e) => updateField(null, 'notes', e.target.value)}
            />
          </Field>
          
          <Field>
            <Label>Terms & Conditions:</Label>
            <TextArea
              rows="3"
              placeholder="Payment terms and conditions"
              value={invoiceData.terms}
              onChange={(e) => updateField(null, 'terms', e.target.value)}
            />
          </Field>
        </NotesSection>
      </InvoiceForm>
    </InvoiceContainer>
  );
};

// Styled Components
const InvoiceContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  background: white;
  min-height: 100vh;
  color: #333;
  
  @media print {
    padding: 0;
    box-shadow: none;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media print {
    display: none;
  }
`;

const Title = styled.h1`
  color: #2c3e50;
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button`
  background: #3498db;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    background: #2980b9;
  }
`;

const PrintButton = styled(Button)`
  background: #27ae60;
  
  &:hover {
    background: #219a52;
  }
`;

const InvoiceForm = styled.div`
  border: 1px solid #ddd;
  padding: 2rem;
  background: white;
`;

const InvoiceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 2rem;
  align-items: flex-start;
`;

const HeaderLeft = styled.div``;

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
`;

const Logo = styled.h2`
  color: #2c3e50;
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
`;

const InvoiceTitle = styled.h1`
  color: #34495e;
  margin: 0;
  font-size: 2rem;
`;

const PartiesContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  margin-bottom: 2rem;
`;

const Party = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const PartyTitle = styled.h3`
  margin: 0 0 0.5rem 0;
  color: #2c3e50;
`;

const PaymentSection = styled.div`
  margin-bottom: 2rem;
`;

const Field = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  min-width: 120px;
  color: #2c3e50;
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  flex: 1;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const TextArea = styled.textarea`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  resize: vertical;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const Select = styled.select`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
`;

const ItemsSection = styled.div`
  margin-bottom: 2rem;
`;

const ItemsHeader = styled.div`
  display: flex;
  background: #f8f9fa;
  border: 1px solid #ddd;
  border-bottom: none;
`;

const HeaderCell = styled.div`
  padding: 0.75rem;
  font-weight: 600;
  border-right: 1px solid #ddd;
  width: ${props => props.width};
  
  &:last-child {
    border-right: none;
  }
`;

const ItemRow = styled.div`
  display: flex;
  border: 1px solid #ddd;
  border-bottom: none;
  
  &:last-child {
    border-bottom: 1px solid #ddd;
  }
`;

const ItemCell = styled.div`
  padding: 0.5rem;
  border-right: 1px solid #ddd;
  width: ${props => props.width};
  display: flex;
  align-items: center;
  
  &:last-child {
    border-right: none;
  }
`;

const AmountDisplay = styled.div`
  font-weight: 600;
  color: #27ae60;
`;

const RemoveButton = styled.button`
  background: #e74c3c;
  color: white;
  border: none;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 1rem;
  
  &:hover {
    background: #c0392b;
  }
`;

const AddItemButton = styled.button`
  background: #27ae60;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  margin-top: 0.5rem;
  
  &:hover {
    background: #219a52;
  }
`;

const TotalSection = styled.div`
  margin-left: auto;
  width: 300px;
  margin-bottom: 2rem;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #eee;
  
  &:last-child {
    border-bottom: 2px solid #2c3e50;
    font-weight: 600;
    font-size: 1.1rem;
  }
`;

const TotalLabel = styled.div`
  color: #2c3e50;
`;

const TotalAmount = styled.div`
  color: #27ae60;
  font-weight: 600;
`;

const NotesSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export default InvoiceGenerator; 